import { MainPaths } from '@routing/main/mainPaths';
import { rootPaths } from '@routing/rootPaths';
import { SettingsPaths } from '@routing/settings/settingsPaths';
import { isEmptyArr, urlEncode } from '@utils/helper';
import {
  getContractsBody,
  getCrmBody,
  getPaymentsBody,
  getTaskBody
} from '@utils/helper/getSearchAndDownloadBodyAllTables';
import { stringifyUrl } from 'query-string';

import { WidgetObjectTypes } from './models';

export function parseJSON(value?: string) {
  if (!value) return {};

  try {
    // eslint-disable-next-line @typescript-eslint/no-implied-eval
    const result = new Function(`return (${value})`)();

    return typeof result === 'object' && result !== null ? result : {};
  } catch (error) {
    return {};
  }
}

export const getFirstParam = <T extends object>(firstEventListParams: T) =>
  Object.keys(firstEventListParams)?.reduce<typeof firstEventListParams>(
    (p, c) => {
      if (Array.isArray(firstEventListParams[c as keyof typeof firstEventListParams])) {
        p[c as keyof typeof firstEventListParams] = (
          firstEventListParams[c as keyof typeof firstEventListParams] as string[]
        ).filter(item => item && item !== 'null' && item !== 'undefined') as any;
      } else {
        p[c as keyof typeof firstEventListParams] = firstEventListParams[c as keyof typeof firstEventListParams] as any;
      }

      return p;
    },
    {} as typeof firstEventListParams
  );

export const getSecondParam = <T extends object, U extends object>(firstEventListParams: T, secondEventListParams: U) =>
  Object.keys(firstEventListParams)?.reduce<typeof firstEventListParams>(
    (p, c) => {
      const key = `two${c.charAt(0).toUpperCase() + c.slice(1)}`;

      if (Array.isArray(secondEventListParams[key as keyof typeof secondEventListParams])) {
        p[c as keyof typeof firstEventListParams] = (
          secondEventListParams[key as keyof typeof secondEventListParams] as string[]
        ).filter(item => item && item !== 'null' && item !== 'undefined') as any;
      } else {
        p[c as keyof typeof firstEventListParams] = secondEventListParams[
          key as keyof typeof secondEventListParams
        ] as any;
      }

      return p;
    },
    {} as typeof firstEventListParams
  );

export const parserLinkFunc = (value: any): any[] =>
  Object.entries(value).map(([key, value]) => {
    if (Array.isArray(value)) {
      return value?.map(item => `${encodeURIComponent(key)}=${encodeURIComponent(item)}`)?.join('&');
    }

    if (key === 'partTwoFilter') {
      return parserLinkFunc(
        Object.keys(value as object)?.reduce(
          (prev, cur) => {
            if (!['page', 'size'].includes(cur)) {
              return {
                ...prev,
                [`two${cur.charAt(0).toUpperCase() + cur.slice(1)}`]: value?.[cur as keyof typeof value]
              };
            }

            return prev;
          },
          {
            isSecondStep: 1
          }
        )
      ).join('&');
    }

    return `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
  });

export function toPascalCase(input?: string): string {
  return (
    input
      ?.toLowerCase()
      // eslint-disable-next-line no-useless-escape
      ?.split(/[\s_\-]+/)
      ?.map(word => word.charAt(0)?.toUpperCase() + word?.slice(1))
      ?.join('') || 'LogClient'
  );
}

export function generateBrightColor(label: any): string {
  let hash = 0;
  const name = String(label);

  for (let i = 0; i < name.length; i++) {
    // eslint-disable-next-line no-bitwise
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }

  const h = Math.abs(hash % 360);
  const s = 70 + (hash % 30);
  const l = 50 + (hash % 20);

  const hslToRgb = (h: number, s: number, l: number): [number, number, number] => {
    s /= 100;
    l /= 100;

    const k = (n: number) => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

    const r = Math.round(255 * f(0));
    const g = Math.round(255 * f(8));
    const b = Math.round(255 * f(4));

    return [r, g, b];
  };

  const [r, g, b] = hslToRgb(h, s, l);

  const toHex = (value: number) => value.toString(16).padStart(2, '0');

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

export function removeNullValues(obj: any) {
  const newObj = { ...obj };

  for (const key in newObj) {
    const value = newObj[key];

    if (Array.isArray(value) && (value.length === 1 || value.length === 0) && (!value[0] || value[0] === null)) {
      delete newObj[key];
    } else if (!value) {
      delete newObj[key];
    }
  }

  return newObj;
}

export const parsePayloadFields = (fields: any) => {
  const parseFields: string[] = [];

  if (fields) {
    Object.keys(fields).forEach(key => {
      if (fields?.[key] && key) {
        if (Array.isArray(fields?.[key])) {
          (fields?.[key] as string[])?.forEach(value => {
            if (value) {
              parseFields.push(urlEncode(`${key}_${value}`));
            }
          });
        } else {
          parseFields.push(urlEncode(`${key}_${fields?.[key]}`));
        }
      }
    });
  }

  return isEmptyArr(parseFields) ? undefined : parseFields;
};

export const parseNavigateLink = ({
  type,
  date,
  payloadData
}: {
  date?: {
    startDate?: string | null;
    finishDate?: string | null;
    responsibleByIds?: (number | null)[] | null;
  };
  type: WidgetObjectTypes;
  payloadData: Record<string, string>;
}) => {
  const objectData = removeNullValues({
    ...{
      ...payloadData,
      ...(payloadData?.pipeId &&
        !payloadData?.voronka && {
          voronka: String(payloadData?.pipeId)
        })
    },
    responsibleByIds: payloadData?.responsibleByIds || date?.responsibleByIds,
    fields: type === 'LEAD' ? parsePayloadFields(payloadData?.fields) : payloadData?.fields
  });

  const query = parserLinkFunc(objectData)
    ?.filter(value => value)
    ?.join('&');

  const startDate = objectData?.startDate || date?.startDate;
  const finishDate = objectData?.finishDate || date?.finishDate;

  const links: Record<string, string> = {
    CONTRACT: stringifyUrl({
      url: `${rootPaths.MAIN}${MainPaths.CONTRACT}?${query}`,
      query: {
        startDate,
        finishDate
      }
    }),
    PAYMENT: stringifyUrl({
      url: `${rootPaths.MAIN}${MainPaths.PAYMENT}?${query}`,
      query: {
        startDate,
        finishDate
      }
    }),
    TASK: stringifyUrl({
      url: `${rootPaths.MAIN}${MainPaths.TASK}?${query}`,
      query: {
        startDate,
        finishDate
      }
    }),
    DEFAULT: stringifyUrl({
      url: `${rootPaths.MAIN}${MainPaths.SETTING}${SettingsPaths.EVET_LIST}?${query}`,
      query: {
        startDate,
        finishDate
      }
    }),
    LEAD: stringifyUrl({
      url: `${rootPaths.MAIN}${MainPaths.CRM}?${query}`,
      query: {
        startDate,
        finishDate
      }
    })
  };

  return links?.[type] || links?.DEFAULT;
};

export function isArrayValuesNotEmpty<T>(array: T[]): boolean {
  return array.every(value => value !== null && value !== undefined && value !== '');
}

export const getParseBody = (searchObj: Record<string, any>, objectType: WidgetObjectTypes) => {
  let parseObject = { ...searchObj };

  if (objectType === 'LEAD') {
    parseObject = getCrmBody(searchObj);
  } else if (objectType === 'CONTRACT') {
    parseObject = getContractsBody(10, searchObj);
  } else if (objectType === 'PAYMENT') {
    parseObject = getPaymentsBody(10, searchObj);
  } else if (objectType === 'TASK') {
    parseObject = getTaskBody(searchObj as never);
  }

  return removeNullValues(parseObject);
};

export const amountFixer = ({ amount, lang }: { amount?: number; lang: string }) => {
  const numbers = {
    mln: 1000000,
    mlrd: 1000000000,
    trln: 1000000000000
  };

  const langNumbers: Record<string, Record<string, string>> = {
    mln: {
      uz: 'mln',
      ru: 'млн',
      en: 'mln',
      tj: 'млн',
      ky: 'млн',
      tr: 'mln'
    },
    mlrd: { uz: 'mlrd', ru: 'млрд', en: 'bln', tj: 'млрд', ky: 'млрд', tr: 'mlyr' },
    trln: { uz: 'trln', ru: 'трлн', en: 'trln', tj: 'трлн', ky: 'трлн', tr: 'trln' }
  };

  if (amount) {
    let result = `${amount}`;

    Object.keys(numbers).forEach(key => {
      const objKey = key as keyof typeof numbers;

      if (Math.abs(amount) >= numbers[objKey]) {
        result = `${parseFloat((amount / numbers[objKey])?.toFixed(2) as never)?.toString()} ${langNumbers?.[objKey]?.[lang]}`;
      }
    });

    return result;
  }

  return '';
};
